import { CollapsibleFilters } from '../../common/CollapsibleFilters';
import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalDialog,
  Option,
  Select,
  Stack,
  Textarea,
  Typography,
} from "@mui/joy";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, downloadApiBlob } from "../../../lib/api";
import type { EmailCampaignStats } from "../../../types/marketing";
import { ResponsiveTable } from "../../common/ResponsiveTable";
import { CampaignStatsCharts } from "./CampaignStatsCharts";
import { emailError, type EmailPage, type SendOutcome } from "./emailWorkflows";
type Report = EmailCampaignStats & {
  totals: Record<string, number>;
  denominators: Record<string, { label: string; count: number }>;
  date_basis: string;
  open_tracking_note: string;
};
export function EmailReport({
  campaignId,
  onClose,
}: {
  campaignId: number;
  onClose: () => void;
}) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [reconcile, setReconcile] = useState<SendOutcome | null>(null);
  const [outcome, setOutcome] = useState("accepted");
  const [note, setNote] = useState("");
  const [messageId, setMessageId] = useState("");
  const [verified, setVerified] = useState(false);
  const [busy, setBusy] = useState(false);
  const cache = useQueryClient();
  const dates = new URLSearchParams({ date_from: from, date_to: to });
  const params = new URLSearchParams({
    campaign: String(campaignId),
    date_from: from,
    date_to: to,
    status,
    search,
    page: String(page),
  });
  const stats = useQuery({
    queryKey: ["email-report", campaignId, from, to],
    queryFn: async () =>
      (
        await api.get<Report>(
          `/api/marketing/email-campaigns/${campaignId}/stats/?${dates}`,
        )
      ).data,
    refetchInterval: 15000,
  });
  const sends = useQuery({
    queryKey: ["email-outcomes", campaignId, from, to, status, search, page],
    queryFn: async () =>
      (
        await api.get<EmailPage<SendOutcome>>(
          `/api/marketing/email-sends/?${params}`,
        )
      ).data,
    refetchInterval: 15000,
  });
  const resolve = async () => {
    if (!reconcile) return;
    setBusy(true);
    setError("");
    try {
      await api.post(`/api/marketing/email-sends/${reconcile.id}/reconcile/`, {
        outcome,
        note,
        provider_verified: verified,
        provider_message_id: messageId,
      });
      setReconcile(null);
      await Promise.all([
        cache.invalidateQueries({ queryKey: ["email-outcomes"] }),
        cache.invalidateQueries({ queryKey: ["email-report"] }),
        cache.invalidateQueries({ queryKey: ["email-quota"] }),
        cache.invalidateQueries({ queryKey: ["email-campaigns"] }),
      ]);
    } catch (cause) {
      setError(emailError(cause));
    }
    setBusy(false);
  };
  return (
    <Modal open onClose={onClose}>
      <ModalDialog
        sx={{
          width: 1120,
          maxWidth: "96vw",
          maxHeight: "94vh",
          overflowY: "auto",
        }}
      >
        <Stack direction="row" justifyContent="space-between">
          <Typography level="h4">Campaign report</Typography>
          <Button variant="plain" onClick={onClose}>
            Close
          </Button>
        </Stack>
        {error && <Alert color="danger">{error}</Alert>}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <FormControl>
            <FormLabel>Attempted from</FormLabel>
            <Input
              type="date"
              value={from}
              onChange={(event) => {
                setFrom(event.target.value);
                setPage(1);
              }}
            />
          </FormControl>
          <FormControl>
            <FormLabel>Attempted through</FormLabel>
            <Input
              type="date"
              value={to}
              onChange={(event) => {
                setTo(event.target.value);
                setPage(1);
              }}
            />
          </FormControl>
          <Button
            variant="outlined"
            onClick={async () => {
              try {
                await downloadApiBlob(
                  `/api/marketing/email-sends/export/?${params}`,
                  "email-recipient-outcomes.csv",
                );
              } catch (cause) {
                setError(emailError(cause));
              }
            }}
          >
            Export recipient CSV
          </Button>
        </Stack>
        {stats.isError && (
          <Alert
            color="danger"
            endDecorator={
              <Button onClick={() => stats.refetch()}>Retry</Button>
            }
          >
            Reporting unavailable.
          </Alert>
        )}
        {stats.data && (
          <>
            <Alert color="neutral">{stats.data.open_tracking_note}</Alert>
            <CampaignStatsCharts stats={stats.data} />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4,1fr)" },
                gap: 1,
              }}
            >
              {Object.entries(stats.data.denominators).map(
                ([key, denominator]) => (
                  <Box
                    key={key}
                    sx={{
                      p: 1,
                      bgcolor: "background.level1",
                      borderRadius: "sm",
                    }}
                  >
                    <Typography level="title-md">
                      {denominator.count
                        ? `${(stats.data!.rates as Record<string, number>)[key]}%`
                        : "—"}
                    </Typography>
                    <Typography level="body-xs">
                      {key.replaceAll("_", " ")} · denominator:{" "}
                      {denominator.count} {denominator.label}
                    </Typography>
                  </Box>
                ),
              )}
            </Box>
            <Typography level="body-sm">
              {stats.data.totals.failed} failed · {stats.data.totals.uncertain}{" "}
              uncertain · {stats.data.totals.dispatching} dispatching ·{" "}
              {stats.data.totals.skipped} quota-held ·{" "}
              {stats.data.totals.excluded} excluded before delivery
            </Typography>
            <Typography level="body-xs">
              Date filter: {stats.data.date_basis}. CSV includes every matching
              recipient page.
            </Typography>
          </>
        )}
        <Typography level="title-lg">Recipient outcomes</Typography>
        <CollapsibleFilters><Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Input
            aria-label="Search recipient email"
            placeholder="Search recipient email"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
          />
          <Select
            value={status}
            aria-label="Outcome status"
            onChange={(_, value) => {
              setStatus(value || "");
              setPage(1);
            }}
          >
            <Option value="">All outcomes</Option>
            {[
              "sent",
              "delivered",
              "opened",
              "clicked",
              "failed",
              "uncertain",
              "dispatching",
              "skipped",
              "suppressed",
              "bounced",
              "complained",
              "unsubscribed",
            ].map((value) => (
              <Option key={value} value={value}>
                {value}
              </Option>
            ))}
          </Select>
        </Stack></CollapsibleFilters>
        {sends.isError && (
          <Alert
            color="danger"
            endDecorator={
              <Button onClick={() => sends.refetch()}>Retry</Button>
            }
          >
            Recipient outcomes unavailable.
          </Alert>
        )}
        <ResponsiveTable
          rows={sends.data?.results || []}
          getRowKey={(send) => send.id}
          columns={[
            {
              key: "email",
              header: "Recipient",
              mobileVariant: "header",
              render: (send) => (
                <Typography sx={{ overflowWrap: "anywhere" }}>
                  {send.email}
                </Typography>
              ),
            },
            {
              key: "status",
              header: "Outcome",
              render: (send) => <Chip size="sm">{send.status}</Chip>,
            },
            {
              key: "attempts",
              header: "Attempts",
              render: (send) => send.attempts,
            },
            {
              key: "sent",
              header: "Accepted at",
              render: (send) =>
                send.sent_at
                  ? new Date(send.sent_at).toLocaleString()
                  : "Not confirmed",
            },
            {
              key: "error",
              header: "Details",
              mobileVariant: "body",
              render: (send) => send.error || send.reconciliation_note || "—",
            },
          ]}
          rowActions={(send) =>
            ["uncertain", "dispatching"].includes(send.status) ? (
              <Button
                size="sm"
                variant="outlined"
                onClick={() => {
                  setReconcile(send);
                  setNote("");
                  setMessageId("");
                  setVerified(false);
                }}
              >
                Reconcile
              </Button>
            ) : null
          }
          pagination={{
            pageSize: 20,
            page,
            totalCount: sends.data?.count || 0,
            onPageChange: setPage,
            itemNoun: "recipients",
          }}
          emptyState={
            <Typography>
              {sends.isPending
                ? "Loading outcomes…"
                : sends.isError
                  ? "Outcomes unavailable."
                  : "No matching recipient attempts."}
            </Typography>
          }
        />
        {reconcile && (
          <Box
            sx={{
              p: 2,
              border: "1px solid",
              borderColor: "warning.outlinedBorder",
              borderRadius: "md",
            }}
          >
            <Stack spacing={1.5}>
              <Typography level="title-lg">
                Reconcile {reconcile.email}
              </Typography>
              <Alert color="warning">
                Check the provider’s records first. Only mark a request “not
                accepted” when the provider confirms it was not accepted;
                retrying an accepted email could create a duplicate. A request
                still in flight cannot be reconciled.
              </Alert>
              <FormControl>
                <FormLabel>Verified provider outcome</FormLabel>
                <Select
                  value={outcome}
                  onChange={(_, value) => setOutcome(value || "accepted")}
                >
                  <Option value="accepted">Accepted by provider</Option>
                  <Option value="not_accepted">Not accepted by provider</Option>
                </Select>
              </FormControl>
              {outcome === "accepted" && (
                <FormControl>
                  <FormLabel>Provider message ID</FormLabel>
                  <Input
                    value={messageId}
                    onChange={(event) => setMessageId(event.target.value)}
                  />
                </FormControl>
              )}
              <FormControl>
                <FormLabel>Evidence and notes</FormLabel>
                <Textarea
                  minRows={3}
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                />
              </FormControl>
              <Checkbox
                checked={verified}
                onChange={(event) => setVerified(event.target.checked)}
                label="I verified this outcome in the provider’s records"
              />
              <Stack direction="row" spacing={1}>
                <Button
                  variant="plain"
                  disabled={busy}
                  onClick={() => setReconcile(null)}
                >
                  Cancel
                </Button>
                <Button
                  loading={busy}
                  disabled={
                    !verified ||
                    note.trim().length < 10 ||
                    (outcome === "accepted" && !messageId.trim())
                  }
                  onClick={resolve}
                >
                  Record verified outcome
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}
      </ModalDialog>
    </Modal>
  );
}
